/**
 * Data hooks for the Agent-page EMAIL tab (C3). Mirror the channel hooks
 * (`useChannels.ts`): fetch + poll + refetch-on-focus, plus a `mail.changed`
 * subscription that is a no-op until the runtime emits it (keeps the mandatory
 * `plugin.` namespace rule in one helper).
 *
 * ⚠ Provisional — see `api/lx-mail.ts`. The email tab stays gated (placeholder)
 * until the runtime `mail.*` methods deploy; then these light up.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { useLexEventStream } from "@/hooks/useEventStream";
import {
  getMail,
  listMail,
  listMailboxes,
  type MailCursor,
  type MailEnvelope,
  type Mailbox,
  type MailMessage,
} from "@/api/lx-mail";

const POLL_MS = 30_000;
const POLL_HIDDEN_MS = 120_000;
/**
 * Rows per mailbox page — a COLD first-paint budget.
 *
 * The runtime memoizes `folder+envelopeId → Message-ID` (`74bab2c8`), so
 * RE-listing a folder costs no IMAP exports at all: measured on galvão at
 * 5–6 s warm (and 3 s for an empty folder, i.e. pure `folder list` +
 * `envelope list` overhead). The FIRST list of a folder still pays one
 * `message export` per row — measured ~3.6 s/row cold (Sent: 20.7 s for 5
 * rows) — so the page size still sets how long the tab sits empty the first
 * time: 8 rows ≈ 31 s cold, 15 rows ≈ 57 s.
 *
 * Hence 8 rather than the runtime's default 25: it halves the only cost the
 * lawyer actually waits on, and "load older" pages the rest at memo speed.
 * Raise it when the per-row export disappears (the envelope listing itself
 * carries no Message-ID — see the runtime module header).
 *
 * NOTE the socket drops seen on 2026-07-21/22 were never about this number:
 * the gateway had no `ping` handler, so every heartbeat went unanswered and
 * any request outliving the client's 8 s window killed the connection. Fixed
 * runtime-side (`f8dfe341`) and hardened client-side (`heartbeatFailureIsFatal`
 * in `lib/gateway-client.ts`).
 */
const PAGE = 8;

function isMethodNotFound(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported|disabled/i.test(msg);
}

function gatewayReady(): boolean {
  return useGatewayStore.getState().connectionState === "connected";
}

/** Shared focus/visibility refetch + optional poll + the future `mail.changed`. */
function useMailAutoRefresh(fetchFn: () => void, poll: boolean): void {
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const tick = () => fnRef.current();
    const schedule = () => {
      if (!poll) return;
      if (timer) clearInterval(timer);
      const hidden =
        typeof document !== "undefined" && document.visibilityState === "hidden";
      timer = setInterval(tick, hidden ? POLL_HIDDEN_MS : POLL_MS);
    };
    schedule();
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
      schedule();
    };
    const onFocus = () => tick();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [poll]);

  useLexEventStream(
    "mail.changed",
    useCallback(() => fnRef.current(), []),
  );
}

export type MailGate = "loading" | "enabled" | "disabled";

export interface UseMailboxesResult {
  mailboxes: Mailbox[];
  gate: MailGate;
  error: string | null;
  refetch: () => Promise<void>;
}

/** `mail.mailboxes` + a gate (same semantics as `useChannelsInventory`):
 *  method-not-found ⇒ `disabled` (mail.* not deployed) → the tab keeps its
 *  placeholder; any other error keeps it enabled + surfaces a pane error. */
export function useMailboxes(opts: { enabled: boolean }): UseMailboxesResult {
  const { enabled } = opts;
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [gate, setGate] = useState<MailGate>("loading");
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const fetchMailboxes = useCallback(async () => {
    if (!enabled || !gatewayReady()) return;
    try {
      const res = await listMailboxes();
      if (disposedRef.current) return;
      setMailboxes(res.mailboxes ?? []);
      setGate("enabled");
      setError(null);
    } catch (err) {
      if (disposedRef.current || !gatewayReady()) return;
      if (isMethodNotFound(err)) {
        setGate("disabled");
        setError(null);
      } else {
        setGate("enabled");
        setError(err instanceof Error ? err.message : "mail unavailable");
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && connectionState === "connected") void fetchMailboxes();
  }, [enabled, connectionState, fetchMailboxes]);

  useMailAutoRefresh(() => void fetchMailboxes(), enabled);

  return { mailboxes, gate, error, refetch: fetchMailboxes };
}

export interface UseMailListResult {
  messages: MailEnvelope[];
  /** Rows the runtime could not read, accumulated across the pages loaded. */
  skipped: number;
  /** Folder size reported by the runtime (before paging); 0 when unknown. */
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  loadOlder: () => Promise<void>;
  refetch: () => Promise<void>;
}

/** `mail.list` for one mailbox — newest first, paged backwards on `nextBefore`. */
export function useMailList(
  mailbox: string | null,
  opts: { enabled: boolean },
): UseMailListResult {
  const { enabled } = opts;
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [messages, setMessages] = useState<MailEnvelope[]>([]);
  const [cursor, setCursor] = useState<MailCursor | undefined>(undefined);
  const [skipped, setSkipped] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);
  const cursorRef = useRef<MailCursor | undefined>(undefined);
  cursorRef.current = cursor;
  const active = Boolean(enabled && mailbox);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const fetchHead = useCallback(async () => {
    if (!active || !mailbox || !gatewayReady()) return;
    setIsLoading(true);
    try {
      const res = await listMail({ mailbox, limit: PAGE });
      if (disposedRef.current) return;
      setMessages(res.messages ?? []);
      setCursor(res.nextBefore);
      setHasMore(res.nextBefore !== undefined);
      setSkipped(res.skipped ?? 0);
      setTotal(res.total ?? 0);
      setError(null);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "mailbox unavailable");
    } finally {
      if (!disposedRef.current) setIsLoading(false);
    }
  }, [active, mailbox]);

  const loadOlder = useCallback(async () => {
    const before = cursorRef.current;
    if (!mailbox || before === undefined || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const res = await listMail({ mailbox, before, limit: PAGE });
      if (disposedRef.current) return;
      // Dedup by messageId on the page boundary.
      setMessages((prev) => {
        const held = new Set(prev.map((m) => m.messageId));
        return [...prev, ...(res.messages ?? []).filter((m) => !held.has(m.messageId))];
      });
      setCursor(res.nextBefore);
      setHasMore(res.nextBefore !== undefined);
      // Skips accumulate: each page reports only its own.
      if (res.skipped) setSkipped((n) => n + res.skipped!);
      if (res.total) setTotal(res.total);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "mailbox unavailable");
    } finally {
      if (!disposedRef.current) setIsLoadingMore(false);
    }
  }, [mailbox, isLoadingMore]);

  useEffect(() => {
    if (active && connectionState === "connected") void fetchHead();
  }, [active, connectionState, fetchHead]);

  // No interval poll (matches useChannelThread): a head refetch replaces the
  // list and would reset a lawyer's "load older" pages mid-read. New mail is
  // surfaced by the mailbox unread badges (useMailboxes polls) + focus/event
  // refetch here; the open list stays put while paging.
  useMailAutoRefresh(() => void fetchHead(), false);

  return { messages, skipped, total, hasMore, isLoading, isLoadingMore, error, loadOlder, refetch: fetchHead };
}

export interface UseMailMessageResult {
  message: MailMessage | null;
  isLoading: boolean;
  error: string | null;
}

/** `mail.get` — the full message for a selected envelope (keyed on Message-ID). */
export function useMailMessage(messageId: string | null): UseMailMessageResult {
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [message, setMessage] = useState<MailMessage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!messageId || connectionState !== "connected") {
      setMessage(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const res = await getMail(messageId);
        if (cancelled || disposedRef.current) return;
        setMessage(res.message);
        setError(null);
      } catch (err) {
        if (cancelled || disposedRef.current) return;
        setError(err instanceof Error ? err.message : "message unavailable");
      } finally {
        if (!cancelled && !disposedRef.current) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messageId, connectionState]);

  return { message, isLoading, error };
}
